import { identifyBrazeUser, startBrazeIfConsented, trackBrazeEvent } from './useBraze';
import type { SubscriptionState } from './index';
import type { Account } from '@/models/account/authModels';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { Preferences } from '@capacitor/preferences';

/**
 * Map user communication preferences to Braze subscription states
 */
export function mapPreferencesToBrazeSubscriptions(account: Account): {
    email?: SubscriptionState;
} {
    // User is opted in to email if ANY of these are true
    const emailOptedIn = account.receiveDonatosNewsAndEventEmails || account.ltdCommunicationEmail || account.lrmEmails || account.receivePersonalizedOffers || account.isMarketingEmail;

    // Only send OPTED_IN — never actively send UNSUBSCRIBED from a sync.
    // Unsubscribes must come from an explicit user action, not account flag evaluation.
    // Push subscription state is managed by Braze via device token registration.
    return {
        email: emailOptedIn ? 'OPTED_IN' : undefined,
    };
}

/**
 * Initialize Braze SDK on app launch and re-identify returning users.
 * syncUserToBraze only runs on explicit login, so returning users with persisted
 * sessions would never get changeUser called. We persist the customerId locally
 * to re-identify on every cold start without an extra API call.
 */
export async function initializeBraze(): Promise<void> {
    const hasConsent = true;
    await startBrazeIfConsented(hasConsent);

    if (Capacitor.isNativePlatform()) {
        const { value: customerId } = await Preferences.get({ key: 'braze_customer_id' });
        if (customerId) {
            await identifyBrazeUser(customerId);
            const permStatus = await PushNotifications.checkPermissions();
            if (permStatus.receive === 'granted') {
                (window as any).BrazePlugin?.setPushNotificationSubscriptionType('opted_in');
            }
        }
    }
}

/**
 * Sync user profile to Braze after login or profile update
 */
export async function syncUserToBraze(account: Account): Promise<void> {
    if (!account.customerId || !account.emailAddress) {
        console.warn('Braze: Cannot sync user without customerId and emailAddress');
        return;
    }

    const subscriptions = mapPreferencesToBrazeSubscriptions(account);

    await identifyBrazeUser(account.customerId, account.emailAddress, subscriptions);

    if (Capacitor.isNativePlatform()) {
        const permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'granted') {
            (window as any).BrazePlugin?.setPushNotificationSubscriptionType('opted_in');
        }
    }
}

/**
 * Track order placement event
 */
export async function trackOrderPlaced(orderId: string, total: number, storeNumber?: string): Promise<void> {
    await trackBrazeEvent('order_placed', {
        order_id: orderId,
        total,
        store: storeNumber || 'Unknown',
    });
}

/**
 * Track user registration event
 */
export async function trackUserRegistered(customerId: string, email: string): Promise<void> {
    await trackBrazeEvent('user_registered', {
        customer_id: customerId,
        email,
    });
}

/**
 * Track communication preference updates
 */
export async function trackPreferenceUpdate(preferenceType: string, optedIn: boolean): Promise<void> {
    await trackBrazeEvent('preference_updated', {
        preference_type: preferenceType,
        opted_in: optedIn,
    });
}

/**
 * Sync guest's email opt-in to Braze
 * Uses the email as the external_id since no customerId is available
 * Only call this when the isMarketingEmail is true.
 */
export async function syncGuestEmailToBraze(email: string): Promise<void> {
    if (!email) return;
    await identifyBrazeUser(email, email, { email: 'OPTED_IN' });
}
